import type {
  HouseholdProfileSummary,
  HouseholdProfilesResponse,
  ProfileBarangayOption,
  RecordDetailResponse,
  IndividualProfileSummary,
} from "@rbim/shared";
import { classifyResidentSex, classifyResidentType } from "@rbim/shared";
import type { AddNewDataFormValues } from "../desktop-add-new-data/modules/add-new-data/lib/schema";
import type {
  RbimFamilyUnit,
  RbimHouseholdProfile,
  RbimRecordImportMode,
  RbimRecordSourceSystem,
  RbimResidentProfile,
} from "../desktop-add-new-data/lib/rbim-storage";
import { resolveResidentBirthdate } from "../desktop-add-new-data/lib/rbim-storage";
import { apiRequest } from "../../api";

export type WebReportData = {
  households: RbimHouseholdProfile[];
  residents: RbimResidentProfile[];
  barangays: ProfileBarangayOption[];
};

type RawQuestionnaire = Partial<AddNewDataFormValues> & Record<string, unknown>;
type RawMember = Record<string, unknown>;

const profilePageSize = 100;
const detailBatchSize = 12;

export async function loadAuthorizedReportData(): Promise<WebReportData> {
  const profilePages = await loadAllHouseholdPages();
  const summaries = profilePages.items;
  const details = new Map<string, RecordDetailResponse>();
  // A canonical household can contain several family envelopes. Every source
  // member still carries its authorized recordId and original memberIndex.
  const recordIds = [...new Set(summaries.flatMap(s => [s.recordId, ...s.members.map(m => m.recordId)]))];

  for (let index = 0; index < recordIds.length; index += detailBatchSize) {
    const batch = recordIds.slice(index, index + detailBatchSize);
    const loaded = await Promise.all(batch.map(async (recordId) => {
      const detail = await apiRequest<RecordDetailResponse>(`/records/${recordId}`);
      return [recordId, detail] as const;
    }));
    loaded.forEach(([recordId, detail]) => details.set(recordId, detail));
  }

  return adaptAuthorizedReportData(summaries, details, profilePages.barangays);
}

export function adaptAuthorizedReportData(summaries: HouseholdProfileSummary[], details: Map<string, RecordDetailResponse>, barangays: ProfileBarangayOption[]): WebReportData {
  const candidates = summaries.flatMap(summary => summary.members.map(profile => ({ summary, profile, detail: details.get(profile.recordId) })));
  const selected = new Map<string, IndividualProfileSummary>();
  // Match server canonical ordering; select latest BEFORE excluding inactive
  // residents so an older active occurrence cannot reappear in reports.
  candidates.sort((a, b) => Date.parse(b.detail?.updatedAt ?? b.profile.updatedAt) - Date.parse(a.detail?.updatedAt ?? a.profile.updatedAt)
    || (a.profile.recordId < b.profile.recordId ? -1 : a.profile.recordId > b.profile.recordId ? 1 : 0) || a.profile.memberIndex - b.profile.memberIndex);
  for (const { summary, profile, detail } of candidates) {
    if (!detail || detail.barangayId !== summary.barangayId) throw new Error("Incomplete authorized report data; reload the report.");
    const member = rawMember(detail, profile.memberIndex);
    if (!member) throw new Error("A report source changed; reload the report.");
    const personKey = `${summary.barangayId}:${stringValue(member.residentId) || `${profile.recordId}:${profile.memberIndex + 1}`}`;
    if (!selected.has(personKey)) selected.set(personKey, profile);
  }
  const activeSources = new Set([...selected.values()].filter(p => p.lifecycleStatus === "ACTIVE").map(p => `${p.recordId}:${p.memberIndex}`));
  const households = summaries
    .map((summary) => adaptRecord(summary, details, activeSources))
    .filter((record): record is RbimHouseholdProfile => Boolean(record));

  return {
    households,
    residents: households.flatMap((household) => household.members),
    barangays,
  };
}

async function loadAllHouseholdPages() {
  const first = await apiRequest<HouseholdProfilesResponse>(
    `/profiles/households?page=1&pageSize=${profilePageSize}&search=`,
  );
  const items = [...first.items];

  for (let page = 2; page <= first.totalPages; page += 1) {
    const next = await apiRequest<HouseholdProfilesResponse>(
      `/profiles/households?page=${page}&pageSize=${profilePageSize}&search=`,
    );
    items.push(...next.items);
  }

  return { items, barangays: first.barangays };
}

function adaptRecord(
  summary: HouseholdProfileSummary,
  details: Map<string, RecordDetailResponse>,
  activeSources: Set<string>,
): RbimHouseholdProfile | null {
  const detail = details.get(summary.recordId);
  if (!detail || !detail.payload || typeof detail.payload !== "object") return null;

  const payload = detail.payload as RawQuestionnaire;
  const household = asRecord(payload.household);
  const householdQuestions = asRecord(payload.householdQuestions);
  const members = summary.members.filter(p => activeSources.has(`${p.recordId}:${p.memberIndex}`));
  if (!members.length) return null;
  const submittedAt = detail.updatedAt || summary.updatedAt;
  const sourceSystem = normalizeSourceSystem(payload.sourceSystem);
  const importMode = normalizeImportMode(payload.importMode, sourceSystem);
  const importStatus = normalizeImportStatus(payload.importStatus);
  const reviewWarnings = Array.isArray(payload.reviewWarnings)
    ? payload.reviewWarnings.map(String)
    : [];
  const householdId = summary.recordId;
  const questionnaireRecord = payload as Record<string, unknown>;
  const municipality = stringValue(household.municipality);
  const barangay = stringValue(household.barangay) || summary.barangayName;
  const province = stringValue(household.province);
  const householdCode = stringValue(household.householdCode) || summary.householdCode;

  const residents = members.map((profile) => adaptMember({
    member: rawMember(details.get(profile.recordId)!, profile.memberIndex)!,
    memberIndex: profile.memberIndex,
    profile,
    sourceFamily: stringValue(asRecord(asRecord(details.get(profile.recordId)!.payload).household).family),
    summary,
    householdId,
    submittedAt,
    householdCode,
    municipality,
    barangay,
    province,
    sourceSystem,
    importMode,
    importStatus,
    reviewWarnings,
    payload: questionnaireRecord,
  }));

  return {
    householdId,
    submissionId: householdId,
    submittedAt,
    householdNumber: householdCode,
    householdCode,
    province,
    cityMunicipality: municipality,
    barangay,
    purok: stringValue(household.purok) || summary.purok,
    address: stringValue(household.address) || summary.address,
    householdHeadName: stringValue(household.householdHead) || summary.householdHead,
    respondentName: stringValue(household.respondentName) || summary.respondentName,
    totalMembers: residents.length,
    familyUnitCount: new Set(residents.map((resident) => resident.familyUnitNo)).size,
    familyUnits: buildFamilyUnits(householdId, submittedAt, residents),
    members: residents,
    sourceSystem,
    importMode,
    importStatus,
    reviewWarnings,
    legacyRawData: asOptionalRecord(payload.legacyRawData),
    importedAt: optionalString(payload.importedAt),
    cleanedData: { ...questionnaireRecord, household, householdQuestions },
    rawSource: questionnaireRecord,
  };
}

function adaptMember(options: {
  member: RawMember;
  memberIndex: number;
  profile: IndividualProfileSummary;
  sourceFamily: string;
  summary: HouseholdProfileSummary;
  householdId: string;
  submittedAt: string;
  householdCode: string;
  municipality: string;
  barangay: string;
  province: string;
  sourceSystem: RbimRecordSourceSystem;
  importMode: RbimRecordImportMode;
  importStatus: RbimResidentProfile["importStatus"];
  reviewWarnings: string[];
  payload: Record<string, unknown>;
}): RbimResidentProfile {
  const { member, memberIndex } = options;
  const firstName = stringValue(member.firstName);
  const middleName = stringValue(member.middleName);
  const lastName = stringValue(member.lastName);
  const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ");
  const resident: RbimResidentProfile = {
    residentId: stringValue(member.residentId) || `${options.profile.recordId}:${memberIndex + 1}`,
    householdId: options.householdId,
    submissionId: options.householdId,
    submittedAt: options.submittedAt,
    lineNumber: memberIndex + 1,
    fullName,
    surname: lastName,
    firstName,
    middleName,
    sex: classifyResidentSex(member.sex) ?? "Invalid",
    dateOfBirth: stringValue(member.dateOfBirth),
    displayBirthdate: stringValue(member.birthdateOriginalValue) || stringValue(member.dateOfBirth),
    birthdatePrecision: normalizeBirthdatePrecision(member.birthdatePrecision),
    ageYears: numberValue(member.reportedAge ?? member.importedAge),
    reportAge: options.profile.age,
    ageMonths: null,
    ageDisplay: "",
    ageBasis: "unknown",
    ageSource: normalizeAgeSource(member.ageSource),
    ageAsOfDate: optionalString(member.ageAsOfDate) ?? null,
    isAgeApproximate: false,
    ageGroup: "",
    relationshipToHouseholdHead: stringValue(member.relationshipToHead),
    familyUnitId: stringValue(member.familyUnitId),
    familyUnitNo: stringValue(member.familyUnitNo) || stringValue(member.familyUnitId) || (/^[A-Z]$/i.test(options.sourceFamily) ? `FAM-${String(options.sourceFamily.toUpperCase().charCodeAt(0) - 64).padStart(3, "0")}` : options.sourceFamily || "FAM-001"),
    familyName: stringValue(member.familyName) || `Family ${options.sourceFamily || "A"}`,
    relationshipToFamilyHead: stringValue(member.relationshipToFamilyHead),
    isFamilyHead: member.isFamilyHead === true,
    civilStatus: stringValue(member.civilStatus),
    educationLevel: stringValue(member.highestEducation),
    schoolAttendance: stringValue(member.currentlyEnrolled),
    occupation: stringValue(member.occupation),
    income: stringValue(member.monthlyIncome),
    sourceOfIncome: stringValue(member.sourceOfIncome),
    workStatus: stringValue(member.workStatus),
    migrationStatus: classifyResidentType(member.residentType),
    healthInsurance: stringValue(member.healthInsurance),
    disability: stringValue(member.disability),
    householdCode: options.householdCode,
    barangay: options.barangay,
    cityMunicipality: options.municipality,
    province: options.province,
    sourceSystem: options.sourceSystem,
    importMode: options.importMode,
    importStatus: options.importStatus,
    reviewWarnings: options.reviewWarnings,
    legacyRawData: asOptionalRecord(options.payload.legacyRawData),
    importedAt: optionalString(options.payload.importedAt),
    cleanedData: member,
    rawSource: member,
  };

  const resolved = resolveResidentBirthdate(resident);
  resident.ageYears = resolved.ageYears;
  resident.ageDisplay = resolved.ageDisplay;
  resident.ageBasis = resolved.ageBasis;
  resident.ageSource = resolved.ageSource;
  resident.ageAsOfDate = resolved.ageAsOfDate;
  resident.displayBirthdate = resolved.displayBirthdate || resident.displayBirthdate;
  resident.birthdatePrecision = resolved.precision;
  resident.isAgeApproximate = resolved.isApproximate;
  return resident;
}

function buildFamilyUnits(householdId: string, timestamp: string, residents: RbimResidentProfile[]): RbimFamilyUnit[] {
  const units = new Map<string, RbimFamilyUnit>();
  residents.forEach((resident) => {
    const unitNo = resident.familyUnitNo || "1";
    const existing = units.get(unitNo);
    if (existing) {
      if (resident.isFamilyHead) {
        existing.familyHeadIndividualId = resident.residentId;
        existing.familyHeadName = resident.fullName;
      }
      return;
    }
    units.set(unitNo, {
      id: resident.familyUnitId || `${householdId}:family:${unitNo}`,
      householdId,
      familyUnitNo: unitNo,
      familyName: resident.familyName || `Family ${unitNo}`,
      familyHeadIndividualId: resident.isFamilyHead ? resident.residentId : undefined,
      familyHeadName: resident.isFamilyHead ? resident.fullName : undefined,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
  });
  return [...units.values()];
}

function normalizeSourceSystem(value: unknown): RbimRecordSourceSystem {
  return String(value ?? "").toLowerCase().includes("old") ? "Old RBIM" : "New RBIM";
}

function normalizeImportMode(value: unknown, source: RbimRecordSourceSystem): RbimRecordImportMode {
  if (String(value ?? "").toLowerCase().includes("legacy") || source === "Old RBIM") return "Legacy Continuation";
  return "New Encoding";
}

function normalizeImportStatus(value: unknown): RbimResidentProfile["importStatus"] {
  const text = String(value ?? "").trim();
  if (text === "Imported with Warning" || text === "Needs Review") return text;
  return "Clean";
}

function normalizeBirthdatePrecision(value: unknown): RbimResidentProfile["birthdatePrecision"] {
  return value === "exact" || value === "month-year" || value === "year-only" || value === "age-only"
    ? value
    : "unknown";
}

function normalizeAgeSource(value: unknown): RbimResidentProfile["ageSource"] {
  return value === "date-of-birth" || value === "reported" ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function rawMember(detail: RecordDetailResponse, index: number): RawMember | null {
  const members = asRecord(detail.payload).members;
  const value = Array.isArray(members) ? members[index] : null;
  return value && typeof value === "object" && !Array.isArray(value) ? value as RawMember : null;
}

function asOptionalRecord(value: unknown) {
  const record = asRecord(value);
  return Object.keys(record).length ? record : undefined;
}

function stringValue(value: unknown) {
  return value === null || value === undefined ? "" : String(value).trim();
}

function optionalString(value: unknown) {
  const text = stringValue(value);
  return text || undefined;
}

function numberValue(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}
